/**
 * Silent push-token registration + a `requestPushPermission` trigger for the prime.
 *
 * Registration and prompting are intentionally separate:
 *   - On sign-in we register the token ONLY if permission is already granted
 *     (re-points the device's token to the current user on account switch).
 *   - The OS prompt is fired by requestPushPermission(), gated behind a soft
 *     pre-prompt at a high-intent moment (see usePushPermissionPrime).
 */
import { useCallback, useEffect, useRef } from 'react'
import { Platform } from 'react-native'
import { useApiClient } from '../../../helpers/apiClient.helper'
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext'
import {
  registerIfPermissionGranted,
  requestPermissionAndRegister,
} from '../helpers/pushRegistration.helper'
import type { ApiClient } from '../helpers/notificationsApi'

export function usePushRegistration(): { requestPushPermission: () => Promise<boolean> } {
  // useApiClient() returns an axios-style caller; ApiClient is its narrowed shape.
  const apiClient = useApiClient() as unknown as ApiClient
  const { isLoaded, isSignedIn } = useSupabaseAuth()
  const apiClientRef = useRef(apiClient)
  apiClientRef.current = apiClient

  // Native, signed-in: register silently if the OS permission is already granted.
  useEffect(() => {
    if (Platform.OS === 'web' || !isLoaded || !isSignedIn) return
    void registerIfPermissionGranted(apiClient)
  }, [apiClient, isLoaded, isSignedIn])

  const requestPushPermission = useCallback(
    () => requestPermissionAndRegister(apiClientRef.current),
    [],
  )

  return { requestPushPermission }
}
